import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Copy } from 'lucide-react';

const EMPTY_TERM = { price: '', currency: 'EUR', discount_pct: '', moq: '', note: '' };

export default function BuyersTab({ buyers, products, onReload }) {
  const [editor, setEditor] = useState(null);

  // Compute once per `products` change instead of on every render (previously ran
  // inside both the Pricing and Notes tabs). Cheap but touched on every buyer edit.
  const activeProducts = useMemo(() => products.filter(p => p.active), [products]);

  const save = async () => {
    if (!editor.email || (!editor.id && !editor.password)) {
      toast.error('Email and password required');
      return;
    }
    try {
      // Sanitize product_terms: drop empty rows
      const terms = {};
      for (const [pid, t] of Object.entries(editor.product_terms || {})) {
        const hasAnything = (t.price ?? '') !== '' || (t.discount_pct ?? '') !== '' || (t.moq ?? '') !== '' || (t.note ?? '') !== '';
        if (hasAnything) {
          terms[pid] = {
            price: t.price || '',
            currency: t.currency || 'EUR',
            discount_pct: t.discount_pct === '' ? null : Number(t.discount_pct),
            moq: t.moq === '' ? null : Number(t.moq),
            note: t.note || '',
          };
        }
      }
      const payload = {
        email: editor.email,
        password: editor.password || 'unchanged',
        name: editor.name || '',
        company: editor.company || '',
        country: editor.country || '',
        phone: editor.phone || '',
        personal_note: editor.personal_note || '',
        product_notes: editor.product_notes || {},
        product_terms: terms,
      };
      if (editor.id) await api.put(`/admin/buyers/${editor.id}`, payload);
      else await api.post('/admin/buyers', payload);
      toast.success('Buyer saved');
      setEditor(null);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this buyer account?')) return;
    await api.delete(`/admin/buyers/${id}`);
    toast.success('Deleted');
    onReload();
  };

  const setTerm = (pid, field, value) => {
    setEditor(prev => ({
      ...prev,
      product_terms: {
        ...prev.product_terms,
        [pid]: { ...(prev.product_terms?.[pid] || EMPTY_TERM), [field]: value },
      },
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif">Buyers</h2>
          <p className="text-sm text-[#3D2817]/60">Reserved-access accounts with personal pricing.</p>
        </div>
        <Button data-testid="new-buyer" onClick={() => setEditor({
          email: '', password: '', name: '', company: '', country: '', phone: '',
          personal_note: '', product_notes: {}, product_terms: {},
        })}>
          <Plus className="w-4 h-4 mr-2" /> New Buyer
        </Button>
      </div>
      <div className="border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Personal terms</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buyers.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#3D2817]/50">No buyer accounts yet.</TableCell></TableRow>
            )}
            {buyers.map(b => (
              <TableRow key={b.id} data-testid={`buyer-row-${b.id}`}>
                <TableCell className="font-serif">{b.company || '—'}</TableCell>
                <TableCell>{b.name || '—'}</TableCell>
                <TableCell>{b.country || '—'}</TableCell>
                <TableCell className="text-xs">{b.email}</TableCell>
                <TableCell className="text-xs text-[#3D2817]/60">
                  {(b.personal_note || '').substring(0, 40)}{(b.personal_note || '').length > 40 ? '…' : ''}
                  {Object.keys(b.product_notes || {}).length > 0 && (
                    <Badge variant="outline" className="ml-2 text-[10px]">{Object.keys(b.product_notes).length} notes</Badge>
                  )}
                  {Object.keys(b.product_terms || {}).length > 0 && (
                    <Badge className="ml-2 text-[10px] bg-[#C5A059] text-white">{Object.keys(b.product_terms).length} priced</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`buyer-duplicate-${b.id}`} variant="ghost" size="icon" title="Duplicate"
                    onClick={() => setEditor({
                      email: '', password: '',
                      name: b.name || '',
                      company: (b.company || '') + ' (copy)',
                      country: b.country || '',
                      phone: b.phone || '',
                      personal_note: b.personal_note || '',
                      product_notes: { ...(b.product_notes || {}) },
                      product_terms: JSON.parse(JSON.stringify(b.product_terms || {})),
                    })}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button data-testid={`buyer-edit-${b.id}`} variant="ghost" size="icon" onClick={() => setEditor({
                    ...b, password: '',
                    product_notes: b.product_notes || {},
                    product_terms: b.product_terms || {},
                  })}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button data-testid={`buyer-delete-${b.id}`} variant="ghost" size="icon" onClick={() => remove(b.id)}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?.id ? 'Edit Buyer' : 'New Buyer'}</DialogTitle></DialogHeader>
          {editor && (
            <Tabs defaultValue="profile">
              <TabsList>
                <TabsTrigger value="profile" data-testid="buyer-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="pricing" data-testid="buyer-tab-pricing">Reserved Pricing</TabsTrigger>
                <TabsTrigger value="notes" data-testid="buyer-tab-notes">Free-text Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="profile" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Email *</Label><Input data-testid="buyer-form-email" type="email" value={editor.email} onChange={e => setEditor({ ...editor, email: e.target.value })} /></div>
                  <div><Label>Password {editor.id ? '(leave blank to keep)' : '*'}</Label><Input data-testid="buyer-form-password" type="text" value={editor.password} onChange={e => setEditor({ ...editor, password: e.target.value })} placeholder={editor.id ? '(unchanged)' : ''} /></div>
                  <div><Label>Company</Label><Input value={editor.company} onChange={e => setEditor({ ...editor, company: e.target.value })} /></div>
                  <div><Label>Contact name</Label><Input value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} /></div>
                  <div><Label>Country</Label><Input value={editor.country} onChange={e => setEditor({ ...editor, country: e.target.value })} /></div>
                  <div><Label>Phone (E.164)</Label><Input value={editor.phone || ''} onChange={e => setEditor({ ...editor, phone: e.target.value })} placeholder="+39123456789" /></div>
                </div>
                <div>
                  <Label>Personal welcome note</Label>
                  <Textarea rows={3} value={editor.personal_note} onChange={e => setEditor({ ...editor, personal_note: e.target.value })}
                    placeholder="e.g. Priority partner. Net 30 terms. Dedicated shade lab access." />
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4 pt-4">
                <p className="text-sm text-[#3D2817]/60">Reserved list price shown to this buyer on catalog + product page. Leave a row empty to hide.</p>
                <div className="border rounded-sm max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F2ECE0] sticky top-0">
                      <tr className="text-left text-xs uppercase tracking-widest text-[#3D2817]/60">
                        <th className="p-2 w-56">Product</th>
                        <th className="p-2 w-24">Price</th>
                        <th className="p-2 w-16">Currency</th>
                        <th className="p-2 w-20">Discount %</th>
                        <th className="p-2 w-20">MOQ</th>
                        <th className="p-2">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeProducts.map(p => {
                        const t = editor.product_terms?.[p.id] || EMPTY_TERM;
                        return (
                          <tr key={p.id} className="border-t">
                            <td className="p-2">
                              <div className="font-mono text-xs text-[#3D2817]/50">{p.sku}</div>
                              <div className="font-serif">{p.translations?.en?.name || p.translations?.it?.name || p.sku}</div>
                            </td>
                            <td className="p-2"><Input data-testid={`term-price-${p.id}`} placeholder="0.00" value={t.price ?? ''} onChange={e => setTerm(p.id, 'price', e.target.value)} /></td>
                            <td className="p-2"><Input value={t.currency ?? 'EUR'} onChange={e => setTerm(p.id, 'currency', e.target.value.toUpperCase())} /></td>
                            <td className="p-2"><Input type="number" placeholder="0" value={t.discount_pct ?? ''} onChange={e => setTerm(p.id, 'discount_pct', e.target.value)} /></td>
                            <td className="p-2"><Input type="number" placeholder="0" value={t.moq ?? ''} onChange={e => setTerm(p.id, 'moq', e.target.value)} /></td>
                            <td className="p-2"><Input placeholder="e.g. FOB Milan, custom shade" value={t.note ?? ''} onChange={e => setTerm(p.id, 'note', e.target.value)} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="notes" className="space-y-4 pt-4">
                <p className="text-sm text-[#3D2817]/60">Optional free-text terms shown on product cards (in addition to reserved pricing).</p>
                <div className="border rounded-sm max-h-96 overflow-y-auto">
                  {activeProducts.map(p => (
                    <div key={p.id} className="p-3 border-b last:border-0 flex items-start gap-3">
                      <div className="w-48 flex-shrink-0">
                        <div className="font-mono text-xs text-[#3D2817]/50">{p.sku}</div>
                        <div className="font-serif text-sm">{p.translations?.en?.name || p.translations?.it?.name || p.sku}</div>
                      </div>
                      <Input placeholder="e.g. Custom shade on request" value={editor.product_notes?.[p.id] || ''}
                        onChange={e => setEditor({ ...editor, product_notes: { ...editor.product_notes, [p.id]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button data-testid="buyer-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
